import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelComparisonRow } from '../../../api/agent-miner-client/agent-miner-client.type';
import { Table } from '../../../components/table/table';
import { getModelComparisonColumns } from './model-comparison-table.columns';

type Props = {
  rows: ModelComparisonRow[];
  costVisible: boolean;
  /** Opens the sessions side panel for the clicked family. */
  onSelectFamily?: (modelFamily: string) => void;
  /** Highlights the row whose sessions the side panel is currently listing. */
  selectedFamily?: string | null;
};

/**
 * The model & version comparison table: one row per (model family x tool),
 * sorted by sessions descending by default. Clicking a row drills into that
 * family's sessions when `onSelectFamily` is supplied.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The rendered table
 */
export const ModelComparisonTable = ({ rows, costVisible, onSelectFamily, selectedFamily }: Props) => {
  const { t } = useTranslation();
  const columns = useMemo(() => getModelComparisonColumns(t, costVisible), [t, costVisible]);

  return (
    <Table<ModelComparisonRow>
      data={rows}
      columns={columns}
      getRowId={(row) => row.model_family}
      onRowClick={onSelectFamily ? (row) => onSelectFamily(row.model_family) : undefined}
      selectedRowId={selectedFamily ?? undefined}
      defaultSortedBy="sessions"
      defaultSortedByDirection="desc"
      totalItems={rows.length}
      stickyHeader
    />
  );
};
